/**
 * @file     SensitivityCard.tsx
 * @purpose  Sensitivity sliders that nudge appreciation, rent growth, and investment return live, with default markers and a reset link.
 */
'use client';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { DashboardSlider } from '../_shared/DashboardSlider';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { defaultRentVsBuyInputs, type RentVsBuyInputs } from './rentVsBuyMath';

type Props = {
  inputs: RentVsBuyInputs;
  patch: (next: Partial<RentVsBuyInputs>) => void;
};

type Row = {
  key: 'appreciationPct' | 'rentGrowthPct' | 'investmentReturnPct';
  label: string;
  min: number;
  max: number;
  step: number;
  suffix: string;
};

const ROWS: Row[] = [
  { key: 'appreciationPct', label: 'Home appreciation', min: -2, max: 8, step: 0.1, suffix: '%' },
  { key: 'rentGrowthPct', label: 'Rent growth', min: 0, max: 8, step: 0.1, suffix: '%' },
  { key: 'investmentReturnPct', label: 'Investment return', min: 0, max: 12, step: 0.1, suffix: '%' },
];

export const SensitivityCard = ({ inputs, patch }: Props) => {
  const { theme } = useAppTheme();
  const defaults = defaultRentVsBuyInputs();

  const onReset = () => {
    patch({
      appreciationPct: defaults.appreciationPct,
      rentGrowthPct: defaults.rentGrowthPct,
      investmentReturnPct: defaults.investmentReturnPct,
    });
  };

  return (
    <Box
      sx={{
        bgcolor: theme.semanticColors.surfaceCard,
        borderRadius: `${theme.layout.radius.md}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.h4,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            letterSpacing: '-0.01em',
          }}
        >
          What if your assumptions change?
        </Typography>
        <ButtonBase
          onClick={onReset}
          sx={{
            cursor: 'pointer',
            color: theme.semanticColors.interactivePrimary,
            fontSize: theme.typography.bodySm,
            fontWeight: 600,
            letterSpacing: '0.01em',
            borderRadius: `${theme.layout.radius.sm}px`,
            px: 0.75,
            py: 0.25,
            transition: `color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}`,
            '&:hover': { textDecoration: 'underline' },
            '&:focus-visible': {
              outline: `2px solid ${theme.semanticColors.interactivePrimary}`,
              outlineOffset: 2,
            },
          }}
        >
          Reset to defaults
        </ButtonBase>
      </Box>
      <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, mb: 2.5 }}>
        Drag to see how your break-even year moves with each assumption.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {ROWS.map((row) => {
          const value = inputs[row.key] as number;
          const dflt = defaults[row.key] as number;
          return (
            <Box key={row.key}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, fontWeight: 500 }}>
                  {row.label}
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: `${theme.layout.radius.md}px`,
                    bgcolor: theme.semanticColors.surfaceElevated,
                    border: `1px solid ${theme.semanticColors.borderSubtle}`,
                    fontSize: theme.typography.bodySm,
                    color: theme.semanticColors.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                  }}
                >
                  {value.toFixed(1)}{row.suffix}
                </Box>
              </Box>
              <DashboardSlider
                value={value}
                min={row.min}
                max={row.max}
                step={row.step}
                marks={[{ value: dflt, label: `${dflt.toFixed(1)}%` }]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${(v as number).toFixed(1)}${row.suffix}`}
                onChange={(_, v) => {
                  const n = Array.isArray(v) ? v[0] : v;
                  patch({ [row.key]: n } as Partial<RentVsBuyInputs>);
                }}
                aria-label={row.label}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
